export const searchProductEventsConfig = {
    parentSelector: '.template-search', // Targeting search page
    events: {
        // Filter accordion events
        filterAccordion: {
            parentSelector: '.facets__disclosure',
            selector: '.facets__summary',
            events: {
                open: {
                    event: 'search', // Event name updated
                    component: 'filter',
                    element: 'accordion',
                    action: 'open', // Updated action to 'open' as per CSV line 21
                    trigger: 'focus',
                    getDynamicData: (element) => {
                        const label = element.querySelector('.facets__summary-label');
                        // Handle price accordion specifically if it lacks the label span
                        const fieldName = label ? label.textContent.trim() : (element.textContent.includes('Price') ? 'price' : 'unknown'); 
                        return {
                            field: fieldName
                        };
                    }
                },
                close: {
                    event: 'search',
                    component: 'filter',
                    element: 'accordion',
                    action: 'close',
                    trigger: 'click',
                    getDynamicData: (element) => {
                        const label = element.querySelector('.facets__summary-label');
                        // Handle price accordion specifically if it lacks the label span
                        const fieldName = label ? label.textContent.trim() : (element.textContent.includes('Price') ? 'price' : 'unknown'); 
                        return {
                            field: fieldName
                        };
                    }
                }
            }
        },

        // Filter checkbox events (Combined on/off)
        filterCheckbox: {
            parentSelector: '.facets__disclosure',
            selector: '.facets__label input[type="checkbox"]',
            event: { // Consolidated into a single event object
                event: 'search', 
                component: 'filter',
                element: 'checkbox',
                // Action is now dynamic
                trigger: 'change',
                getDynamicData: (element) => {
                    const isChecked = element.checked;
                    const action = isChecked ? 'on' : 'off'; // Determine action dynamically

                    const disclosure = element.closest('.facets__disclosure');
                    const summaryElement = disclosure?.querySelector('.facets__summary'); // Find summary to get label
                    // Get field name from summary label or fallback to price if it's the price accordion
                    const summaryLabel = summaryElement?.querySelector('.facets__summary-label');
                    const fieldName = summaryLabel 
                        ? summaryLabel.textContent.trim() 
                        : (summaryElement?.textContent.includes('Price') ? 'price' : 'unknown');

                    const checkboxLabelElement = element.closest('.facets__label')?.querySelector('.facet-checkbox__text-label');
                    const checkboxValue = checkboxLabelElement ? checkboxLabelElement.textContent.trim() : element.value; // Fallback to value

                    return {
                         action: action, // Add dynamic action
                         field: fieldName, 
                         checkbox_value: checkboxValue
                    };
                }
            }
        },

        // Filter price input events
        filterPrice: {
            parentSelector: '.facets__price',
            selector: '.field__input',
            events: {
                focus: {
                    event: 'search', // Event name updated
                    component: 'filter',
                    element: 'text_field',
                    field: 'price', // Updated to match CSV 'field'
                    action: 'focus',
                    trigger: 'focus'
                },
                input: {
                    event: 'search', // Event name updated
                    component: 'filter',
                    element: 'text_field',
                    field: 'price', // Updated to match CSV 'field'
                    action: 'input',
                    trigger: 'input',
                    getDynamicData: (element) => ({
                        field_input_value: element.value
                    })
                }
            }
        },

        // filter reset events
        filterReset: {
            parentSelector: '.facets__disclosure .facets__header, #FacetsWrapperMobile .mobile-facets__footer', // Added mobile selector and adjusted scope
            selector: '.facets__reset, .mobile-facets__clear', // Include mobile clear button
            event: {
                event: 'search', // Event name updated
                component: 'filter',
                element: 'link',
                 // field_name should be dynamically determined or removed if always 'all'
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => ({
                     // Reset often clears a specific filter group. Need logic to determine which one if possible.
                     // For now, just capture click text. Consider if 'field_name' is needed/possible.
                     action_value: element.textContent.trim()
                 })
            }
        },

        // Sorting dropdown events
        sorting: {
            parentSelector: '.facet-filters__field, .mobile-facets__sort', // Added mobile selector
            selector: '#SortBy, #SortBy-mobile', // Added mobile ID
            events: {
                // Note: CSV does not explicitly list open/close for sorting dropdown, only select
                 open: {
                     event: 'search', // Event name updated
                     component: 'sorting',
                     element: 'dropdown',
                     action: 'open', // Changed from toggled for clarity, adjust if CSV differs
                     trigger: 'focus',
                 },
                select: {
                    event: 'search', // Event name updated
                    component: 'sorting',
                    element: 'dropdown',
                    action: 'select',
                    trigger: 'change',
                    getDynamicData: (element) => {
                        return {
                            action_value: element.options[element.selectedIndex].text.trim()
                        };
                    }
                }
            }
        },

        // Product card events
        productCard: {
            parentSelector: '#product-grid', // More specific parent
            selector: '.card__heading a.full-unstyled-link[id^="CardLink-"]', // Specific link inside heading
            event: {
                event: 'search', // Event name updated
                component: 'product_card',
                element: 'card',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                     const headingElement = element.querySelector('.card__heading h3, .card__heading h5'); // Handle different heading levels if necessary
                     return {
                         field: element.textContent.trim(), // Text of the link itself is usually the product name
                         link_url: element.href
                    };
                }
            }
        },

        // Product card CTA events (Assuming no CTA on search results cards based on provided HTML)
         // productCardCTA: { ... } // Keep commented unless CTAs exist

        // Pagination events
        pagination: {
            parentSelector: '.pagination__list',
            events: { // Using 'events' structure as multiple selectors/actions exist
                next: {
                     selector: '.pagination__item--next .link, .pagination__item-arrow a[aria-label="Next page"]', // Updated selectors for next page link/arrow
                     event: 'search', // Event name updated
                     component: 'pagination',
                     element: 'chevron',
                     action: 'click',
                     trigger: 'click',
                     action_value: 'next'
                 },
                previous: {
                    selector: '.pagination__item--prev .link, .pagination__item-arrow a[aria-label="Previous page"]', // Updated selectors for previous page link/arrow
                     event: 'search', // Event name updated
                     component: 'pagination',
                     element: 'chevron',
                     action: 'click',
                     trigger: 'click',
                     action_value: 'previous'
                },
                number: {
                     selector: '.pagination__item:not(.pagination__item--current):not(.pagination__item--prev):not(.pagination__item--next) a.link', // Specific number links
                     event: 'search', // Event name updated
                     component: 'pagination',
                     element: 'number',
                     action: 'click',
                     trigger: 'click',
                     getDynamicData: (element) => ({
                         action_value: element.textContent.trim()
                     })
                 }
            }
        },

        // Filter remove events
        filterRemove: {
             parentSelector: '.active-facets', 
             selector: '.active-facets__button span.active-facets__button-inner', // Updated selector to target the inner span directly
             event: {
                 event: 'search',
                 component: 'filter',
                 element: 'filter',
                 action: 'click',
                 trigger: 'click',
                 getDynamicData: (element) => { // Element is now the span.active-facets__button-inner
                    let filterName = element.textContent.trim();
                    
                    // Clean up the text - remove the hidden "Remove filter" text if present
                    const hiddenText = element.querySelector('.visually-hidden')?.textContent.trim();
                    if (hiddenText && filterName.endsWith(hiddenText)) {
                        filterName = filterName.substring(0, filterName.length - hiddenText.length).trim();
                    }
                    
                    return {
                         filter_name: filterName, 
                         action_value: 'remove' 
                    };
                 }
             }
         },

        filterRemoveAll: {
             parentSelector: '.active-facets',
             selector: '.active-facets__button-remove span, .mobile-facets__clear', // Updated selector to target inner span for desktop
             event: {
                 event: 'search',
                 component: 'filter',
                 element: 'link',
                 action: 'click',
                 trigger: 'click',
                 getDynamicData: (element) => ({ // Element will be the span on desktop, or the clear button on mobile
                     action_value: element.textContent.trim()
                 })
             }
         }
    }
}; 